import React, { useState } from "react";

interface ShareButtonsProps {
  url?: string;          // defaults to window.location.href
  title: string;
  text?: string;         // optional excerpt for share text
  hashtags?: string[];   // optional list, used by Twitter/X
}

/**
 * Social share row used on blog articles.
 * Uses canonical share URLs that don't require any third-party SDK or tracking script.
 * Includes a "Copy link" action and the Web Share API for mobile when available.
 */
const ShareButtons: React.FC<ShareButtonsProps> = ({ url, title, text, hashtags = [] }) => {
  const [copied, setCopied] = useState(false);

  const shareUrl =
    url || (typeof window !== "undefined" ? window.location.href : "https://axoix.com");
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(text || title);
  const tagString = hashtags.join(",");

  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}${
      tagString ? `&hashtags=${encodeURIComponent(tagString)}` : ""
    }`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
    reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers — fallback to a temp input
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // give up silently
      }
      document.body.removeChild(ta);
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      try {
        await (navigator as any).share({ title, text, url: shareUrl });
      } catch {
        // user cancelled — no-op
      }
    }
  };

  const hasNativeShare =
    typeof navigator !== "undefined" && typeof (navigator as any).share === "function";

  const buttonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.04)",
    color: "#fff",
    transition: "all 0.2s",
    textDecoration: "none",
    cursor: "pointer",
    fontSize: 16,
  };

  return (
    <div
      className="share-buttons mt-40"
      style={{
        padding: "20px 24px",
        background: "rgba(0,255,151,0.04)",
        border: "1px solid rgba(0,255,151,0.15)",
        borderRadius: 16,
      }}
    >
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 16 }}>
        <span style={{ fontWeight: 600, color: "#fff", fontSize: 14 }}>
          <i className="fas fa-share-alt me-2" style={{ color: "var(--color-primary)" }}></i>
          Share this
        </span>
        <div className="d-flex flex-wrap" style={{ gap: 10 }}>
          <a
            href={links.facebook}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            title="Share on Facebook"
            style={buttonStyle}
          >
            <i className="fab fa-facebook-f"></i>
          </a>
          <a
            href={links.twitter}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on X (Twitter)"
            title="Share on X (Twitter)"
            style={buttonStyle}
          >
            <i className="fab fa-twitter"></i>
          </a>
          <a
            href={links.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
            title="Share on LinkedIn"
            style={buttonStyle}
          >
            <i className="fab fa-linkedin-in"></i>
          </a>
          <a
            href={links.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on WhatsApp"
            title="Share on WhatsApp"
            style={buttonStyle}
          >
            <i className="fab fa-whatsapp"></i>
          </a>
          <a
            href={links.telegram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Telegram"
            title="Share on Telegram"
            style={buttonStyle}
          >
            <i className="fab fa-telegram-plane"></i>
          </a>
          <a
            href={links.reddit}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Reddit"
            title="Share on Reddit"
            style={buttonStyle}
          >
            <i className="fab fa-reddit-alien"></i>
          </a>
          <a
            href={links.email}
            aria-label="Share via Email"
            title="Share via Email"
            style={buttonStyle}
          >
            <i className="far fa-envelope"></i>
          </a>
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? "Link copied" : "Copy link"}
            title={copied ? "Link copied!" : "Copy link"}
            style={{
              ...buttonStyle,
              borderColor: copied ? "var(--color-primary)" : "rgba(255,255,255,0.15)",
              color: copied ? "var(--color-primary)" : "#fff",
            }}
          >
            <i className={copied ? "fas fa-check" : "far fa-copy"}></i>
          </button>
          {hasNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              aria-label="Share with system menu"
              title="Share…"
              style={buttonStyle}
            >
              <i className="fas fa-ellipsis-h"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareButtons;
